import axios from 'axios';
import { url } from '../client_config/config';

const VIEW_AS_COMPANY_KEY = 'viewAsCompany';

function readStoredViewAsCompany() {
  try {
    const raw = localStorage.getItem(VIEW_AS_COMPANY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed._id) {
      return { _id: String(parsed._id), name: parsed.name || 'Company' };
    }
  } catch (e) {
    // ignore corrupt storage
  }
  return null;
}

export default {
  namespaced: true,
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!(localStorage.getItem('token') && localStorage.getItem('user')),
    loading: false,
    error: null,
    viewAsCompany: readStoredViewAsCompany()
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
      state.isAuthenticated = !!user;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
      }
    },
    SET_TOKEN(state, token) {
      state.token = token;
      if (token) {
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Only set authenticated if we also have user data
        state.isAuthenticated = !!state.user;
      } else {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        state.isAuthenticated = false;
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    SET_VIEW_AS_COMPANY(state, company) {
      if (company && company._id) {
        state.viewAsCompany = {
          _id: String(company._id),
          name: company.name || 'Company'
        };
        localStorage.setItem(VIEW_AS_COMPANY_KEY, JSON.stringify(state.viewAsCompany));
      } else {
        state.viewAsCompany = null;
        localStorage.removeItem(VIEW_AS_COMPANY_KEY);
      }
    },
    CLEAR_VIEW_AS_COMPANY(state) {
      state.viewAsCompany = null;
      localStorage.removeItem(VIEW_AS_COMPANY_KEY);
    },
    LOGOUT(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.viewAsCompany = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem(VIEW_AS_COMPANY_KEY);
      delete axios.defaults.headers.common['Authorization'];
    }
  },
  actions: {
    async register({ commit }, userData) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await axios.post(`${url}/auth/register`, userData);
        const { token, user } = response.data;
        
        commit('SET_TOKEN', token);
        commit('SET_USER', user);
        
        return { success: true, user };
      } catch (error) {
        let errorMessage = 'Registration failed';
        
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          // Handle validation errors
          const validationErrors = error.response.data.errors
            .map(err => `${err.path}: ${err.msg}`)
            .join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async login({ commit }, credentials) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        // Clear any existing tokens before login
        commit('LOGOUT');
        
        const response = await axios.post(`${url}/auth/login`, credentials);
        
        // Validate response structure
        if (!response.data || !response.data.token || !response.data.user) {
          throw new Error('Invalid response from server');
        }
        
        const { token, user } = response.data;
        
        commit('SET_TOKEN', token);
        commit('SET_USER', user);
        
        return { success: true, user };
      } catch (error) {
        // Ensure we're logged out on error
        commit('LOGOUT');
        const errorMessage = error.response?.data?.message || error.message || 'Login failed';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getProfile({ commit }) {
      if (!this.state.auth.token) return;
      
      try {
        const response = await axios.get(`${url}/auth/me`);
        commit('SET_USER', response.data.user);
      } catch (error) {
        console.error('Failed to get profile:', error);
        commit('LOGOUT');
      }
    },

    async updateProfile({ commit }, profileData) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await axios.put(`${url}/auth/profile`, profileData);
        commit('SET_USER', response.data.user);
        return { success: true, user: response.data.user };
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Profile update failed';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async changePassword({ commit }, passwordData) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        await axios.put(`${url}/auth/change-password`, passwordData);
        return { success: true };
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Password change failed';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    updateUser({ commit }, userData) {
      commit('SET_USER', userData);
    },

    setViewAsCompany({ commit }, company) {
      commit('SET_VIEW_AS_COMPANY', company);
    },

    clearViewAsCompany({ commit }) {
      commit('CLEAR_VIEW_AS_COMPANY');
    },

    logout({ commit }) {
      commit('LOGOUT');
    },

    async initializeAuth({ commit, dispatch, state }) {
      const token = localStorage.getItem('token');
      if (token) {
        commit('SET_TOKEN', token);
        try {
          await dispatch('getProfile');
          // Return successful auth result with user data
          return {
            authenticated: true,
            user: state.user
          };
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          // Clear invalid token
          commit('LOGOUT');
          // Return failed auth result
          return {
            authenticated: false,
            user: null,
            error
          };
        }
      } else {
        // No token, ensure we're logged out
        commit('LOGOUT');
        // Return unauthenticated result
        return {
          authenticated: false,
          user: null
        };
      }
    }
  },
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    user: state => state.user,
    token: state => state.token,
    isSuperAdmin: state => state.user?.userType === 'super_admin',
    /** True when super admin is not currently viewing as a company (platform mode). */
    isSuperAdminMode: state =>
      state.user?.userType === 'super_admin' && !state.viewAsCompany,
    isAdmin: state => state.user?.userType === 'admin',
    isJudge: state => state.user?.userType === 'judge',
    isPerformer: state => state.user?.userType === 'performer',
    userType: state => state.user?.userType,
    userFullName: state => {
      if (!state.user) return '';
      return `${state.user.firstName} ${state.user.lastName}`;
    },
    companyName: (state, getters) => {
      if (getters.isViewingAsCompany) {
        return state.viewAsCompany.name || '';
      }
      return state.user?.company?.name || '';
    },
    viewAsCompany: state => state.viewAsCompany,
    isViewingAsCompany: state =>
      !!(state.user?.userType === 'super_admin' && state.viewAsCompany?._id),
    viewAsCompanyId: (state, getters) =>
      getters.isViewingAsCompany ? state.viewAsCompany._id : null,
    viewAsCompanyName: (state, getters) =>
      getters.isViewingAsCompany ? state.viewAsCompany.name : null,
    effectiveCompanyId: (state, getters) => {
      if (getters.isViewingAsCompany) {
        return state.viewAsCompany._id;
      }
      const company = state.user?.company;
      return company != null ? (company._id || company) : null;
    },
    hasError: state => !!state.error
  }
};
